<script>
import { ref } from 'vue';
import { mapGetters } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import Spinner from 'shared/components/Spinner.vue';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import AddLabel from 'shared/components/ui/dropdown/AddLabel.vue';
import {
  cleanLabelTitle,
  groupLabelsByCustomAttribute,
} from 'dashboard/helper/commons.js';

export default {
  components: {
    Spinner,
    LabelDropdown,
    AddLabel,
  },
  props: {
    // Rail card "ETIQUETAS": hide the labels synced from Tipo / Estado
    // (they are already shown as chips) and only offer the other labels.
    onlyOtherLabels: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isAdmin } = useAdmin();

    const {
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
    } = useConversationLabels();

    const showSearchDropdownLabel = ref(false);

    const toggleLabels = () => {
      showSearchDropdownLabel.value = !showSearchDropdownLabel.value;
    };

    const closeDropdownLabel = () => {
      showSearchDropdownLabel.value = false;
    };

    const keyboardEvents = {
      KeyL: {
        action: e => {
          e.preventDefault();
          toggleLabels();
        },
      },
      Escape: {
        action: () => {
          if (showSearchDropdownLabel.value) {
            toggleLabels();
          }
        },
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);
    return {
      isAdmin,
      savedLabels,
      activeLabels,
      accountLabels,
      addLabelToConversation,
      removeLabelFromConversation,
      showSearchDropdownLabel,
      closeDropdownLabel,
      toggleLabels,
      cleanLabelTitle,
    };
  },
  data() {
    return {
      selectedLabels: [],
    };
  },

  computed: {
    ...mapGetters({
      conversationUiFlags: 'conversationLabels/getUIFlags',
      contactAttributes: 'attributes/getContactAttributes',
    }),
    groupedActiveLabels() {
      const grouped = groupLabelsByCustomAttribute(
        this.activeLabels,
        this.contactAttributes
      );
      return this.onlyOtherLabels ? { ...grouped, groups: [] } : grouped;
    },
    selectableAccountLabels() {
      if (!this.onlyOtherLabels) return this.accountLabels;
      return this.accountLabels.filter(
        label => !label.custom_attribute_definition_id && !label.managed
      );
    },
  },
  mounted() {
    if (!this.contactAttributes.length) {
      this.$store.dispatch('attributes/get');
    }
  },
};
</script>

<template>
  <div class="sidebar-labels-wrap">
    <div
      v-if="!conversationUiFlags.isFetching"
      class="contact-conversation--list"
    >
      <div
        v-on-clickaway="closeDropdownLabel"
        class="label-wrap flex flex-wrap"
        @keyup.esc="closeDropdownLabel"
      >
        <div class="flex flex-col w-full gap-2">
          <div
            v-for="group in groupedActiveLabels.groups"
            :key="group.id"
            class="flex flex-col gap-1"
          >
            <span
              class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              {{ group.name }}
            </span>
            <div class="flex flex-wrap gap-1">
              <woot-label
                v-for="label in group.labels"
                :key="label.id"
                :title="cleanLabelTitle(label)"
                :description="label.description"
                :show-close="!label.managed"
                :color="label.color"
                variant="smooth"
                class="max-w-[calc(100%-0.5rem)]"
                @remove="removeLabelFromConversation(label.title)"
              />
            </div>
          </div>
          <div
            v-if="groupedActiveLabels.otherLabels.length"
            class="flex flex-col gap-1"
          >
            <span
              class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
            >
              {{ $t('CONTACT_PANEL.LABELS.CONTACT.OTHERS_HEADER') }}
            </span>
            <div class="flex flex-wrap gap-1">
              <woot-label
                v-for="label in groupedActiveLabels.otherLabels"
                :key="label.id"
                :title="cleanLabelTitle(label)"
                :description="label.description"
                show-close
                :color="label.color"
                variant="smooth"
                class="max-w-[calc(100%-0.5rem)]"
                @remove="removeLabelFromConversation(label.title)"
              />
            </div>
          </div>
          <AddLabel @add="toggleLabels" />
        </div>

        <div
          :class="{
            'block visible': showSearchDropdownLabel,
            'hidden invisible': !showSearchDropdownLabel,
          }"
          class="border rounded-lg bg-n-alpha-3 top-6 backdrop-blur-[100px] absolute w-full shadow-lg border-n-strong dark:border-n-strong p-2 box-border z-[9999]"
        >
          <LabelDropdown
            v-if="showSearchDropdownLabel"
            :account-labels="selectableAccountLabels"
            :contact-attributes="contactAttributes"
            :selected-labels="savedLabels"
            :allow-creation="isAdmin"
            @add="addLabelToConversation"
            @remove="removeLabelFromConversation"
          />
        </div>
      </div>
    </div>
    <Spinner v-else />
  </div>
</template>

<style lang="scss" scoped>
.sidebar-labels-wrap {
  margin-bottom: 0;
}
.contact-conversation--list {
  width: 100%;

  .label-wrap {
    line-height: 1.5rem;
    position: relative;
  }
}
</style>
